<template>
  <div class="business-detail">
    <n-button @click="$router.push('/businesses')" style="margin-bottom: 16px">
      Back to Businesses
    </n-button>

    <!-- Business Info -->
    <n-card title="Business Information">
      <template #header-extra>
        <n-button type="primary" size="small" @click="openEditBusinessModal">
          Edit
        </n-button>
      </template>
      <n-descriptions :column="2" label-placement="left">
        <n-descriptions-item label="Name">{{ business.name }}</n-descriptions-item>
        <n-descriptions-item label="Phone">{{ business.phone || '-' }}</n-descriptions-item>
        <n-descriptions-item label="Email">{{ business.email || '-' }}</n-descriptions-item>
        <n-descriptions-item label="ID">{{ business.id }}</n-descriptions-item>
        <n-descriptions-item label="Tax Regime">{{ business.tax_regime === 'gst' ? 'GST' : 'Non-GST' }}</n-descriptions-item>
        <n-descriptions-item label="GSTIN">{{ business.gstin || '-' }}</n-descriptions-item>
        <n-descriptions-item label="State Code">{{ business.state_code || '-' }}</n-descriptions-item>
        <n-descriptions-item label="Address">{{ business.address || '-' }}</n-descriptions-item>
      </n-descriptions>
    </n-card>

    <!-- Users Section -->
    <n-card title="Users" style="margin-top: 16px">
      <template #header-extra>
        <n-button type="primary" size="small" @click="showUserModal = true">
          Add User
        </n-button>
      </template>

      <n-data-table :columns="userColumns" :data="users" :loading="loading" />
    </n-card>

    <!-- Modules Section -->
    <n-card title="Modules" style="margin-top: 16px">
      <n-space vertical>
        <div v-for="mod in allModules" :key="mod.value" class="module-row">
          <n-space justify="space-between" align="center" style="width: 100%">
            <span>{{ mod.label }}</span>
            <n-switch
              :value="isModuleEnabled(mod.value)"
              @update:value="(val) => toggleModule(mod.value, val)"
              :loading="moduleLoading[mod.value]"
            />
          </n-space>
        </div>
      </n-space>
    </n-card>

    <!-- Module Settings (Cashloan) -->
    <n-card title="Module Settings" style="margin-top: 16px" v-if="cashloanLicense">
      <div class="settings-section">
        <h4 style="margin: 0 0 12px 0; font-weight: 500;">Cashloan</h4>
        <n-space align="center">
          <span style="min-width: 120px;">Max Collectors:</span>
          <n-input-number
            v-model:value="cashloanSettings.max_collectors"
            :min="0"
            :max="100"
            placeholder="0 = Unlimited"
            style="width: 140px;"
          />
          <n-button
            type="primary"
            size="small"
            :loading="savingSettings"
            @click="saveCashloanSettings"
          >
            Save
          </n-button>
        </n-space>
        <p style="margin: 8px 0 0 0; font-size: 12px; color: #999;">
          Set to 0 for unlimited collectors. Only collectors with login credentials count against this limit.
        </p>
      </div>
    </n-card>

    <!-- Seva WhatsApp Automation Settings -->
    <n-card title="Seva WhatsApp Automation" style="margin-top: 16px" v-if="isSevaEnabled">
      <n-spin :show="loadingSevaSettings">
        <n-space vertical size="large">
          <div class="setting-row">
            <n-space justify="space-between" align="center" style="width: 100%">
              <div>
                <span style="font-weight: 500;">Auto-send Reminders</span>
                <p style="margin: 4px 0 0 0; font-size: 12px; color: #999;">
                  Automatically send birthday, anniversary, and lapsed donor reminders
                </p>
              </div>
              <n-switch v-model:value="sevaSettings.auto_send_reminders" />
            </n-space>
          </div>
          <div class="setting-row">
            <n-space justify="space-between" align="center" style="width: 100%">
              <div>
                <span style="font-weight: 500;">Auto-send Donation Thanks</span>
                <p style="margin: 4px 0 0 0; font-size: 12px; color: #999;">
                  Automatically send thank you message when a donation is recorded
                </p>
              </div>
              <n-switch v-model:value="sevaSettings.auto_send_donation_thanks" />
            </n-space>
          </div>
          <div class="setting-row">
            <n-space justify="space-between" align="center" style="width: 100%">
              <div>
                <span style="font-weight: 500;">Auto-send 80G Receipt</span>
                <p style="margin: 4px 0 0 0; font-size: 12px; color: #999;">
                  Automatically send 80G tax receipt PDF via WhatsApp after generating
                </p>
              </div>
              <n-switch v-model:value="sevaSettings.auto_send_80g_receipt" />
            </n-space>
          </div>
          <n-button
            type="primary"
            :loading="savingSevaSettings"
            @click="saveSevaSettings"
          >
            Save Settings
          </n-button>
        </n-space>
      </n-spin>
    </n-card>

    <!-- Collectors Section (only when cashloan enabled) -->
    <n-card title="Collectors" style="margin-top: 16px" v-if="isCashloanEnabled">
      <template #header-extra>
        <n-button type="primary" size="small" @click="showCollectorModal = true">
          Add Collector
        </n-button>
      </template>
      <n-data-table :columns="collectorColumns" :data="collectors" :loading="loadingCollectors" />
      <p v-if="!loadingCollectors && collectors.length === 0" style="color: #999; text-align: center; padding: 16px 0;">
        No collectors found for this business
      </p>
    </n-card>

    <!-- Reset Collector Password Modal -->
    <n-modal v-model:show="showResetPasswordModal" preset="dialog" title="Reset Collector Password">
      <n-space vertical size="large" v-if="selectedCollector">
        <p>Reset password for <strong>{{ selectedCollector.name }}</strong>?</p>
        <n-form-item label="New Password">
          <n-input
            v-model:value="newPassword"
            type="password"
            placeholder="Enter new password (min 6 characters)"
            show-password-on="click"
          />
        </n-form-item>
      </n-space>
      <template #action>
        <n-space justify="end">
          <n-button @click="showResetPasswordModal = false">Cancel</n-button>
          <n-button
            type="primary"
            :loading="resettingPassword"
            :disabled="newPassword.length < 6"
            @click="handleResetPassword"
          >
            Reset Password
          </n-button>
        </n-space>
      </template>
    </n-modal>

    <!-- Reset User Password Modal -->
    <n-modal v-model:show="showResetUserPasswordModal" preset="dialog" title="Reset User Password">
      <n-space vertical size="large" v-if="selectedUser">
        <p>Reset password for <strong>{{ selectedUser.name }}</strong> ({{ selectedUser.phone }})?</p>
        <n-form-item label="New Password">
          <n-input
            v-model:value="userNewPassword"
            type="password"
            placeholder="Enter new password (min 6 characters)"
            show-password-on="click"
          />
        </n-form-item>
      </n-space>
      <template #action>
        <n-space justify="end">
          <n-button @click="showResetUserPasswordModal = false">Cancel</n-button>
          <n-button
            type="primary"
            :loading="resettingUserPassword"
            :disabled="userNewPassword.length < 6"
            @click="handleResetUserPassword"
          >
            Reset Password
          </n-button>
        </n-space>
      </template>
    </n-modal>

    <!-- Add Collector Modal -->
    <n-modal v-model:show="showCollectorModal" preset="dialog" title="Add Collector">
      <n-form :model="collectorForm" label-placement="left" label-width="140">
        <n-form-item label="Name" required>
          <n-input v-model:value="collectorForm.name" placeholder="Enter collector name" />
        </n-form-item>
        <n-form-item label="Phone Number" required>
          <n-input v-model:value="collectorForm.phone" placeholder="Enter 10 digit phone" maxlength="10" :allow-input="allowOnlyDigits" />
        </n-form-item>
        <n-form-item label="Area Assigned">
          <n-input v-model:value="collectorForm.area_assigned" placeholder="Optional: Enter area/route" />
        </n-form-item>
        <n-divider>Login Credentials</n-divider>
        <n-form-item label="Password" required>
          <n-input
            v-model:value="collectorForm.password"
            type="password"
            placeholder="Min 6 characters"
            show-password-on="click"
          />
        </n-form-item>
        <p style="font-size: 12px; color: #999; margin: -8px 0 16px 140px;">
          Collector will login with phone + password
        </p>
      </n-form>
      <template #action>
        <n-space justify="end">
          <n-button @click="showCollectorModal = false">Cancel</n-button>
          <n-button
            type="primary"
            :loading="savingCollector"
            :disabled="!collectorForm.name || !collectorForm.phone || collectorForm.phone.length !== 10 || collectorForm.password.length < 6"
            @click="handleCreateCollector"
          >
            Create Collector
          </n-button>
        </n-space>
      </template>
    </n-modal>

    <!-- Licenses -->
    <n-card title="Licenses" style="margin-top: 16px">
      <template #header-extra>
        <n-button type="primary" size="small" @click="showLicenseModal = true">
          Add License
        </n-button>
      </template>

      <n-data-table :columns="licenseColumns" :data="business.licenses || []" :loading="loading" />
    </n-card>

    <!-- Edit Business Modal -->
    <n-modal v-model:show="showEditBusinessModal" preset="dialog" title="Edit Business">
      <n-form ref="editBusinessFormRef" :model="editBusinessForm" :rules="editBusinessFormRules" label-placement="left" label-width="120">
        <n-form-item label="Business Name" path="name">
          <n-input v-model:value="editBusinessForm.name" placeholder="Enter business name" />
        </n-form-item>
        <n-form-item label="Phone" path="phone">
          <n-input v-model:value="editBusinessForm.phone" placeholder="Enter 10 digit phone" maxlength="10" :allow-input="allowOnlyDigits" />
        </n-form-item>
        <n-form-item label="Email" path="email">
          <n-input v-model:value="editBusinessForm.email" placeholder="Enter email" />
        </n-form-item>
        <n-form-item label="Tax Regime">
          <n-select
            v-model:value="editBusinessForm.tax_regime"
            :options="taxRegimeOptions"
            placeholder="Select tax regime"
          />
        </n-form-item>
        <n-form-item v-if="editBusinessForm.tax_regime === 'gst'" label="GSTIN" path="gstin">
          <n-input v-model:value="editBusinessForm.gstin" placeholder="Enter GSTIN" />
        </n-form-item>
        <n-form-item label="State Code">
          <n-input v-model:value="editBusinessForm.state_code" placeholder="e.g., TN, KA" />
        </n-form-item>
        <n-form-item label="Address">
          <n-input v-model:value="editBusinessForm.address" type="textarea" placeholder="Enter address" />
        </n-form-item>
      </n-form>
      <template #action>
        <n-space justify="end">
          <n-button @click="showEditBusinessModal = false">Cancel</n-button>
          <n-button type="primary" :loading="savingBusiness" @click="handleUpdateBusiness">
            Save Changes
          </n-button>
        </n-space>
      </template>
    </n-modal>

    <!-- Add User Modal -->
    <n-modal v-model:show="showUserModal" preset="dialog" title="Add User">
      <n-form ref="userFormRef" :model="userForm" :rules="userFormRules" label-placement="left" label-width="140">
        <n-form-item label="Phone Number" path="phone">
          <n-input v-model:value="userForm.phone" placeholder="Enter 10 digit phone" maxlength="10" :allow-input="allowOnlyDigits" />
        </n-form-item>
        <n-form-item label="Name" path="name">
          <n-input v-model:value="userForm.name" placeholder="Enter display name" />
        </n-form-item>
        <n-form-item label="Username" path="username">
          <n-input v-model:value="userForm.username" placeholder="Optional (defaults to phone)" />
        </n-form-item>
        <n-form-item label="Password" path="password">
          <n-input v-model:value="userForm.password" type="password" placeholder="Enter password (min 6 chars)" show-password-on="click" />
        </n-form-item>
        <n-form-item label="Role" path="role">
          <n-select v-model:value="userForm.role" :options="roleOptions" placeholder="Select role" />
        </n-form-item>
        <n-form-item label="Allowed Modules" path="allowed_modules">
          <n-select
            v-model:value="userForm.allowed_modules"
            :options="moduleOptions"
            multiple
            placeholder="Select modules"
          />
        </n-form-item>
      </n-form>
      <template #action>
        <n-space justify="end">
          <n-button @click="showUserModal = false">Cancel</n-button>
          <n-button type="primary" :loading="saving" @click="handleCreateUser">
            Create User
          </n-button>
        </n-space>
      </template>
    </n-modal>

    <!-- Edit User Modal -->
    <n-modal v-model:show="showEditUserModal" preset="dialog" title="Edit User">
      <n-form :model="editUserForm" label-placement="left" label-width="140">
        <n-form-item label="Phone Number">
          <n-input v-model:value="editUserForm.phone" placeholder="Enter 10 digit phone" maxlength="10" :allow-input="allowOnlyDigits" />
        </n-form-item>
        <n-form-item label="Name">
          <n-input v-model:value="editUserForm.name" placeholder="Enter display name" />
        </n-form-item>
        <n-form-item label="Role">
          <n-select v-model:value="editUserForm.role" :options="roleOptions" placeholder="Select role" />
        </n-form-item>
        <n-form-item label="Allowed Modules">
          <n-select
            v-model:value="editUserForm.allowed_modules"
            :options="moduleOptions"
            multiple
            placeholder="Select modules"
          />
        </n-form-item>
        <n-form-item label="Active">
          <n-switch v-model:value="editUserForm.is_active" />
        </n-form-item>
      </n-form>
      <template #action>
        <n-space justify="end">
          <n-button @click="showEditUserModal = false">Cancel</n-button>
          <n-button type="primary" :loading="saving" @click="handleUpdateUser">
            Save Changes
          </n-button>
        </n-space>
      </template>
    </n-modal>

    <!-- Add License Modal -->
    <n-modal v-model:show="showLicenseModal" preset="dialog" title="Add License">
      <n-form ref="formRef" :model="licenseForm" label-placement="left" label-width="120">
        <n-form-item label="Module" path="module_name">
          <n-select
            v-model:value="licenseForm.module_name"
            :options="moduleOptions"
            placeholder="Select module"
          />
        </n-form-item>
        <n-form-item label="Start Date" path="start_date">
          <n-date-picker v-model:value="licenseForm.start_date" type="date" />
        </n-form-item>
        <n-form-item label="Expiry Date" path="expiry_date">
          <n-date-picker v-model:value="licenseForm.expiry_date" type="date" />
        </n-form-item>
        <n-form-item label="Status" path="status">
          <n-select
            v-model:value="licenseForm.status"
            :options="statusOptions"
            placeholder="Select status"
          />
        </n-form-item>
        <n-divider>Payment (Optional)</n-divider>
        <n-form-item label="Amount Paid" path="amount_paid">
          <n-input-number v-model:value="licenseForm.amount_paid" :min="0" placeholder="0" />
        </n-form-item>
        <n-form-item label="Payment Date" path="payment_date">
          <n-date-picker v-model:value="licenseForm.payment_date" type="date" />
        </n-form-item>
        <n-form-item label="Payment Method" path="payment_method">
          <n-select
            v-model:value="licenseForm.payment_method"
            :options="paymentMethodOptions"
            placeholder="Select method"
          />
        </n-form-item>
      </n-form>
      <template #action>
        <n-button @click="showLicenseModal = false">Cancel</n-button>
        <n-button type="primary" :loading="saving" @click="handleCreateLicense">
          Create License
        </n-button>
      </template>
    </n-modal>

    <!-- Renew License Modal -->
    <n-modal v-model:show="showRenewModal" preset="dialog" title="Renew License">
      <n-form :model="renewForm" label-placement="left" label-width="120">
        <n-form-item label="New Expiry Date">
          <n-date-picker v-model:value="renewForm.new_expiry_date" type="date" />
        </n-form-item>
        <n-form-item label="Amount Paid">
          <n-input-number v-model:value="renewForm.amount_paid" :min="0" />
        </n-form-item>
        <n-form-item label="Payment Date">
          <n-date-picker v-model:value="renewForm.payment_date" type="date" />
        </n-form-item>
        <n-form-item label="Payment Method">
          <n-select
            v-model:value="renewForm.payment_method"
            :options="paymentMethodOptions"
            placeholder="Select method"
          />
        </n-form-item>
      </n-form>
      <template #action>
        <n-button @click="showRenewModal = false">Cancel</n-button>
        <n-button type="primary" :loading="saving" @click="handleRenewLicense">
          Renew
        </n-button>
      </template>
    </n-modal>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, h } from 'vue'
import { useRoute } from 'vue-router'
import {
  NCard,
  NButton,
  NDescriptions,
  NDescriptionsItem,
  NDataTable,
  NModal,
  NForm,
  NFormItem,
  NSelect,
  NDatePicker,
  NInputNumber,
  NDivider,
  NTag,
  NSpace,
  NSwitch,
  NInput,
  NSpin,
} from 'naive-ui'
import { useToast } from '@shared/composables/useToast.js'
import { useAdmin } from '../composables/useAdmin'
import { allowOnlyDigits, emailRules } from '@shared/utils/validators'
import dayjs from 'dayjs'

const route = useRoute()
const toast = useToast()
const { getBusiness, updateBusiness, createLicense, renewLicense, updateLicense, createUser, listUsers, updateUser, resetUserPassword, enableModule, disableModule, listModules, listAvailableModules, listCollectors, createCollector, resetCollectorPassword, getSevaSettings, updateSevaSettings, loading } = useAdmin()

const business = ref({})
const users = ref([])
const enabledModules = ref([])
const collectors = ref([])
const loadingCollectors = ref(false)
const showLicenseModal = ref(false)
const showRenewModal = ref(false)
const showUserModal = ref(false)
const showEditUserModal = ref(false)
const showEditBusinessModal = ref(false)
const editingUser = ref(null)
const saving = ref(false)
const savingBusiness = ref(false)
const selectedLicenseId = ref(null)
const moduleLoading = reactive({})

// Collector state
const showCollectorModal = ref(false)
const collectorForm = ref({
  name: '',
  phone: '',
  area_assigned: '',
  password: '',
})
const savingCollector = ref(false)

// Collector reset password state
const showResetPasswordModal = ref(false)
const selectedCollector = ref(null)
const newPassword = ref('')
const resettingPassword = ref(false)

// User reset password state
const showResetUserPasswordModal = ref(false)
const selectedUser = ref(null)
const userNewPassword = ref('')
const resettingUserPassword = ref(false)

// Module Settings state
const cashloanSettings = ref({ max_collectors: 0 })
const savingSettings = ref(false)

// Seva WhatsApp Automation state
const sevaSettings = ref({
  auto_send_reminders: false,
  auto_send_donation_thanks: false,
  auto_send_80g_receipt: false,
})
const loadingSevaSettings = ref(false)
const savingSevaSettings = ref(false)

// Edit Business state
const editBusinessForm = ref({
  name: '',
  phone: '',
  email: '',
  tax_regime: 'non_gst',
  gstin: '',
  state_code: '',
  address: '',
})

const taxRegimeOptions = [
  { label: 'Non-GST', value: 'non_gst' },
  { label: 'GST', value: 'gst' },
]

const editBusinessFormRef = ref(null)
const editBusinessFormRules = {
  name: { required: true, message: 'Business name is required', trigger: 'blur' },
  phone: [
    { pattern: /^\d{10}$/, message: 'Enter 10 digit phone number', trigger: 'blur' }
  ],
  email: emailRules,
  gstin: {
    validator: (rule, value) => {
      if (editBusinessForm.value.tax_regime === 'gst' && !value) {
        return new Error('GSTIN is required for GST businesses')
      }
      return true
    },
    trigger: 'blur',
  },
}

const userForm = ref({
  phone: '',
  name: '',
  username: '',
  password: '',
  role: 'admin',
  allowed_modules: [],
})
const userFormRef = ref(null)

const editUserForm = ref({
  phone: '',
  name: '',
  role: 'admin',
  allowed_modules: [],
  is_active: true,
})

const userFormRules = {
  phone: [
    { required: true, message: 'Phone number is required', trigger: 'blur' },
    { pattern: /^[0-9]{10}$/, message: 'Enter 10 digit phone number', trigger: 'blur' }
  ],
  name: { required: true, message: 'Name is required', trigger: 'blur' },
  password: { required: true, message: 'Password is required (min 6 chars)', trigger: 'blur', min: 6 },
  role: { required: true, message: 'Role is required', trigger: 'change' },
}

const licenseForm = ref({
  module_name: null,
  start_date: Date.now(),
  expiry_date: null,
  status: 'active',
  amount_paid: 0,
  payment_date: Date.now(),
  payment_method: 'cash',
})

const renewForm = ref({
  new_expiry_date: null,
  amount_paid: 0,
  payment_date: Date.now(),
  payment_method: 'cash',
})

// Available modules loaded from API
const allModules = ref([])

const moduleOptions = computed(() => allModules.value)

const roleOptions = [
  { label: 'Admin', value: 'admin' },
  { label: 'Staff', value: 'staff' },
]

const statusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Trial', value: 'trial' },
]

const paymentMethodOptions = [
  { label: 'Cash', value: 'cash' },
  { label: 'Bank Transfer', value: 'bank_transfer' },
  { label: 'Cheque', value: 'cheque' },
  { label: 'UPI', value: 'upi' },
  { label: 'Online', value: 'online' },
]

// Computed: Find active cashloan license
const cashloanLicense = computed(() =>
  (business.value.licenses || []).find(l => l.module_name === 'cashloan' && l.status === 'active')
)

// Computed: Check if cashloan module is enabled
const isCashloanEnabled = computed(() =>
  enabledModules.value.some(m => m.module_name === 'cashloan' && m.is_enabled)
)

// Computed: Check if seva module is enabled
const isSevaEnabled = computed(() =>
  enabledModules.value.some(m => m.module_name === 'seva' && m.is_enabled)
)

// Watch cashloan license to sync settings from metadata
watch(cashloanLicense, (license) => {
  if (license?.metadata?.max_collectors !== undefined) {
    cashloanSettings.value.max_collectors = license.metadata.max_collectors
  } else {
    cashloanSettings.value.max_collectors = 0
  }
}, { immediate: true })

// Save cashloan module settings
const saveCashloanSettings = async () => {
  if (!cashloanLicense.value) return
  savingSettings.value = true
  try {
    await updateLicense(
      cashloanLicense.value.id,
      { metadata: { max_collectors: cashloanSettings.value.max_collectors } },
      route.params.id
    )
    toast.success('Settings saved')
    await loadBusiness()
  } catch (error) {
    toast.error(error.message)
  } finally {
    savingSettings.value = false
  }
}

// Load seva settings
const loadSevaSettings = async () => {
  if (!isSevaEnabled.value) return
  loadingSevaSettings.value = true
  try {
    const settings = await getSevaSettings(route.params.id)
    sevaSettings.value = {
      auto_send_reminders: settings.auto_send_reminders || false,
      auto_send_donation_thanks: settings.auto_send_donation_thanks || false,
      auto_send_80g_receipt: settings.auto_send_80g_receipt || false,
    }
  } catch (error) {
    console.error('Failed to load seva settings:', error)
  } finally {
    loadingSevaSettings.value = false
  }
}

// Save seva settings
const saveSevaSettings = async () => {
  savingSevaSettings.value = true
  try {
    await updateSevaSettings(route.params.id, sevaSettings.value)
    toast.success('Seva settings saved')
  } catch (error) {
    toast.error(error.message || 'Failed to save settings')
  } finally {
    savingSevaSettings.value = false
  }
}

const userColumns = [
  { title: 'Name', key: 'name' },
  { title: 'Phone', key: 'phone', width: 120 },
  { title: 'Username', key: 'username' },
  { title: 'Role', key: 'role', render: (row) => h(NTag, { type: row.role === 'admin' ? 'info' : 'default', size: 'small' }, () => row.role) },
  { title: 'Modules', key: 'allowed_modules', render: (row) => (row.allowed_modules || []).join(', ') || '-' },
  { title: 'Active', key: 'is_active', render: (row) => h(NTag, { type: row.is_active ? 'success' : 'error', size: 'small' }, () => row.is_active ? 'Yes' : 'No') },
  {
    title: 'Actions',
    key: 'actions',
    width: 180,
    render: (row) => row.is_super_admin
      ? null
      : h(NSpace, { size: 'small' }, () => [
          h(NButton, { size: 'small', type: 'primary', ghost: true, onClick: () => openEditUserModal(row) }, () => 'Edit'),
          h(NButton, { size: 'small', type: 'warning', ghost: true, onClick: () => openResetUserPasswordModal(row) }, () => 'Reset Password'),
        ])
  },
]

const licenseColumns = [
  { title: 'Module', key: 'module_name' },
  {
    title: 'Start Date',
    key: 'start_date',
    render: (row) => dayjs(row.start_date).format('MMM DD, YYYY'),
  },
  {
    title: 'Expiry Date',
    key: 'expiry_date',
    render: (row) => dayjs(row.expiry_date).format('MMM DD, YYYY'),
  },
  {
    title: 'Status',
    key: 'status',
    render: (row) => {
      if (row.is_expired) {
        return h(NTag, { type: 'error', size: 'small' }, () => 'Expired')
      }
      if (row.days_remaining <= 30) {
        return h(NTag, { type: 'warning', size: 'small' }, () => `${row.days_remaining} days left`)
      }
      return h(NTag, { type: 'success', size: 'small' }, () => row.status)
    },
  },
  {
    title: 'Actions',
    key: 'actions',
    render: (row) => {
      return h(NSpace, { size: 'small' }, () => [
        h(
          NButton,
          {
            size: 'small',
            type: 'primary',
            onClick: (e) => {
              e.stopPropagation()
              openRenewModal(row)
            },
          },
          () => 'Renew'
        ),
      ])
    },
  },
]

const collectorColumns = [
  { title: 'Name', key: 'name' },
  { title: 'Phone', key: 'phone', width: 140 },
  { title: 'Area', key: 'area_assigned' },
  {
    title: 'Login',
    key: 'has_login',
    width: 80,
    render: (row) => h(NTag, { type: row.has_login ? 'success' : 'default', size: 'small' }, () => row.has_login ? 'Yes' : 'No')
  },
  {
    title: 'Status',
    key: 'is_active',
    width: 80,
    render: (row) => h(NTag, { type: row.is_active ? 'success' : 'error', size: 'small' }, () => row.is_active ? 'Active' : 'Inactive')
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 120,
    render: (row) => row.has_login
      ? h(NButton, { size: 'small', type: 'primary', ghost: true, onClick: () => openResetPasswordModal(row) }, () => 'Reset Password')
      : h('span', { style: 'color: #999; font-size: 12px;' }, 'No login')
  },
]

const isModuleEnabled = (moduleName) => {
  return enabledModules.value.some((m) => m.module_name === moduleName && m.is_enabled)
}

const toggleModule = async (moduleName, enable) => {
  moduleLoading[moduleName] = true
  try {
    if (enable) {
      await enableModule(route.params.id, moduleName)
      toast.success(`${moduleName} module enabled`)
    } else {
      await disableModule(route.params.id, moduleName)
      toast.success(`${moduleName} module disabled`)
    }
    await loadModules()
  } catch (error) {
    toast.error(error.message)
  } finally {
    moduleLoading[moduleName] = false
  }
}

const openRenewModal = (license) => {
  selectedLicenseId.value = license.id
  renewForm.value = {
    new_expiry_date: dayjs(license.expiry_date).add(1, 'year').valueOf(),
    amount_paid: 0,
    payment_date: Date.now(),
    payment_method: 'cash',
  }
  showRenewModal.value = true
}

const openEditBusinessModal = () => {
  editBusinessForm.value = {
    name: business.value.name || '',
    phone: business.value.phone || '',
    email: business.value.email || '',
    tax_regime: business.value.tax_regime || 'non_gst',
    gstin: business.value.gstin || '',
    state_code: business.value.state_code || '',
    address: business.value.address || '',
  }
  showEditBusinessModal.value = true
}

const handleUpdateBusiness = async () => {
  try {
    await editBusinessFormRef.value?.validate()
  } catch {
    return
  }

  savingBusiness.value = true
  try {
    const data = {}
    // Only include changed fields
    if (editBusinessForm.value.name !== business.value.name) data.name = editBusinessForm.value.name
    if (editBusinessForm.value.phone !== (business.value.phone || '')) data.phone = editBusinessForm.value.phone
    if (editBusinessForm.value.email !== (business.value.email || '')) data.email = editBusinessForm.value.email
    if (editBusinessForm.value.tax_regime !== business.value.tax_regime) data.tax_regime = editBusinessForm.value.tax_regime
    if (editBusinessForm.value.gstin !== (business.value.gstin || '')) data.gstin = editBusinessForm.value.gstin
    if (editBusinessForm.value.state_code !== (business.value.state_code || '')) data.state_code = editBusinessForm.value.state_code
    if (editBusinessForm.value.address !== (business.value.address || '')) data.address = editBusinessForm.value.address

    await updateBusiness(route.params.id, data)
    toast.success('Business updated successfully')
    showEditBusinessModal.value = false
    await loadBusiness()
  } catch (error) {
    toast.error(error.message || 'Failed to update business')
  } finally {
    savingBusiness.value = false
  }
}

const handleCreateUser = async () => {
  try {
    await userFormRef.value?.validate()
  } catch {
    return
  }

  saving.value = true
  try {
    await createUser(route.params.id, userForm.value)
    toast.success('User created successfully')
    showUserModal.value = false
    userForm.value = { phone: '', name: '', username: '', password: '', role: 'admin', allowed_modules: [] }
    await loadUsers()
  } catch (error) {
    toast.error(error.message)
  } finally {
    saving.value = false
  }
}

const openEditUserModal = (user) => {
  editingUser.value = user
  editUserForm.value = {
    phone: user.phone,
    name: user.name,
    role: user.role,
    allowed_modules: user.allowed_modules || [],
    is_active: user.is_active,
  }
  showEditUserModal.value = true
}

const handleUpdateUser = async () => {
  saving.value = true
  try {
    await updateUser(route.params.id, editingUser.value.id, editUserForm.value)
    toast.success('User updated successfully')
    showEditUserModal.value = false
    editingUser.value = null
    await loadUsers()
  } catch (error) {
    toast.error(error.message)
  } finally {
    saving.value = false
  }
}

const openResetUserPasswordModal = (user) => {
  selectedUser.value = user
  userNewPassword.value = ''
  showResetUserPasswordModal.value = true
}

const handleResetUserPassword = async () => {
  if (!selectedUser.value || userNewPassword.value.length < 6) return
  resettingUserPassword.value = true
  try {
    await resetUserPassword(route.params.id, selectedUser.value.id, userNewPassword.value)
    toast.success(`Password reset for ${selectedUser.value.name}`)
    showResetUserPasswordModal.value = false
    selectedUser.value = null
    userNewPassword.value = ''
  } catch (error) {
    toast.error(error.message || 'Failed to reset password')
  } finally {
    resettingUserPassword.value = false
  }
}

const handleCreateLicense = async () => {
  saving.value = true
  try {
    const data = {
      business_id: parseInt(route.params.id),
      module_name: licenseForm.value.module_name,
      start_date: dayjs(licenseForm.value.start_date).format('YYYY-MM-DD'),
      expiry_date: dayjs(licenseForm.value.expiry_date).format('YYYY-MM-DD'),
      status: licenseForm.value.status,
    }

    if (licenseForm.value.amount_paid > 0) {
      data.amount_paid = licenseForm.value.amount_paid
      data.payment_date = dayjs(licenseForm.value.payment_date).format('YYYY-MM-DD')
      data.payment_method = licenseForm.value.payment_method
    }

    await createLicense(data)
    toast.success('License created successfully')
    showLicenseModal.value = false
    loadBusiness()
  } catch (error) {
    toast.error(error.message)
  } finally {
    saving.value = false
  }
}

const handleRenewLicense = async () => {
  saving.value = true
  try {
    await renewLicense(selectedLicenseId.value, business.value.id, {
      new_expiry_date: dayjs(renewForm.value.new_expiry_date).format('YYYY-MM-DD'),
      amount_paid: renewForm.value.amount_paid,
      payment_date: dayjs(renewForm.value.payment_date).format('YYYY-MM-DD'),
      payment_method: renewForm.value.payment_method,
    })
    toast.success('License renewed successfully')
    showRenewModal.value = false
    loadBusiness()
  } catch (error) {
    toast.error(error.message)
  } finally {
    saving.value = false
  }
}

const loadBusiness = async () => {
  try {
    business.value = await getBusiness(route.params.id)
  } catch (error) {
    console.error('Failed to load business:', error)
  }
}

const loadUsers = async () => {
  try {
    const response = await listUsers(route.params.id)
    users.value = response.users || []
  } catch (error) {
    console.error('Failed to load users:', error)
  }
}

const loadModules = async () => {
  try {
    const response = await listModules(route.params.id)
    enabledModules.value = response.modules || []
  } catch (error) {
    console.error('Failed to load modules:', error)
  }
}

// Load all available modules from the centralized registry
const loadAvailableModules = async () => {
  try {
    const response = await listAvailableModules()
    // Transform API response to dropdown format
    allModules.value = (response.modules || []).map(m => ({
      label: m.display_name,
      value: m.name,
    }))
  } catch (error) {
    console.error('Failed to load available modules:', error)
    // Fallback to empty array - user will see no options
  }
}

const loadCollectors = async () => {
  if (!isCashloanEnabled.value) return
  loadingCollectors.value = true
  try {
    const response = await listCollectors(route.params.id)
    collectors.value = response.collectors || []
  } catch (error) {
    console.error('Failed to load collectors:', error)
  } finally {
    loadingCollectors.value = false
  }
}

const handleCreateCollector = async () => {
  savingCollector.value = true
  try {
    const data = {
      name: collectorForm.value.name,
      phone: collectorForm.value.phone,
    }
    if (collectorForm.value.area_assigned) {
      data.area_assigned = collectorForm.value.area_assigned
    }
    if (collectorForm.value.password && collectorForm.value.password.length >= 6) {
      data.password = collectorForm.value.password
    }

    const response = await createCollector(route.params.id, data)

    // Show warning if existing user was reused
    if (response.existing_user_used) {
      toast.warning(response.message || 'Linked to existing user identity')
    }

    toast.success('Collector created successfully')
    showCollectorModal.value = false
    collectorForm.value = { name: '', phone: '', area_assigned: '', password: '' }
    await loadCollectors()
  } catch (error) {
    toast.error(error.message || 'Failed to create collector')
  } finally {
    savingCollector.value = false
  }
}

const openResetPasswordModal = (collector) => {
  selectedCollector.value = collector
  newPassword.value = ''
  showResetPasswordModal.value = true
}

const handleResetPassword = async () => {
  if (!selectedCollector.value || newPassword.value.length < 6) return
  resettingPassword.value = true
  try {
    await resetCollectorPassword(route.params.id, selectedCollector.value.id, newPassword.value)
    toast.success(`Password reset for ${selectedCollector.value.name}`)
    showResetPasswordModal.value = false
    selectedCollector.value = null
    newPassword.value = ''
  } catch (error) {
    toast.error(error.message || 'Failed to reset password')
  } finally {
    resettingPassword.value = false
  }
}

// Watch for cashloan being enabled to load collectors
watch(isCashloanEnabled, (enabled) => {
  if (enabled) {
    loadCollectors()
  } else {
    collectors.value = []
  }
})

// Watch for seva being enabled to load settings
watch(isSevaEnabled, (enabled) => {
  if (enabled) {
    loadSevaSettings()
  } else {
    sevaSettings.value = { auto_send_reminders: false, auto_send_donation_thanks: false, auto_send_80g_receipt: false }
  }
})

onMounted(async () => {
  // Load available modules from registry first (for dropdowns)
  await loadAvailableModules()
  await loadBusiness()
  await loadUsers()
  await loadModules()
  // Load collectors if cashloan is already enabled
  if (isCashloanEnabled.value) {
    await loadCollectors()
  }
  // Load seva settings if seva is already enabled
  if (isSevaEnabled.value) {
    await loadSevaSettings()
  }
})
</script>

<style scoped>
.business-detail {
  max-width: 1000px;
}

.module-row {
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
}

.module-row:last-child {
  border-bottom: none;
}

.setting-row {
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.setting-row:last-of-type {
  border-bottom: none;
}
</style>
